import { Link, type LinkProps } from "@tanstack/react-router";
import React, { useState } from "react";

type DockItemBase = {
  id: string;
  icon: React.ReactNode;
  label: string;
};

export type DockItem = DockItemBase &
  (
    | { onClick: () => void; href?: never }
    | {
        href: LinkProps["to"];
        search?: Record<string, string>;
        onClick?: never;
      }
  );

interface DockItemProps {
  item: DockItem;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  variant?: "default" | "header";
}

const DockItemComponent: React.FC<DockItemProps> = ({
  item,
  isHovered,
  onHover,
  variant = "default"
}) => {
  const isHeader = variant === "header";

  const sharedClassName = `relative flex items-center justify-center ${isHeader ? "h-11 w-11 md:h-9 md:w-9" : "h-11 w-11"} rounded-lg ${
    isHeader
      ? isHovered
        ? "border border-gray-300/70 bg-gray-200/70"
        : "border-transparent bg-transparent"
      : "border border-white/10 bg-white/5 backdrop-blur-[2px]"
  } cursor-pointer shadow-none transition-all duration-300 ease-out ${
    isHovered
      ? isHeader
        ? "-translate-y-1 scale-110 shadow-md"
        : "-translate-y-1 scale-110 border-white/20 bg-white/10 shadow-lg shadow-white/10"
      : isHeader
        ? "hover:-translate-y-0.5 hover:scale-105 hover:border-gray-300/60 hover:bg-gray-200/60"
        : "hover:bg-white/7 hover:-translate-y-0.5 hover:scale-105"
  } ${isHeader ? "text-gray-700" : "text-white/80"} transition-colors duration-200`;

  const sharedStyle: React.CSSProperties = {
    boxShadow: isHovered
      ? isHeader
        ? "0 4px 12px 0 rgba(0,0,0,0.1)"
        : "0 4px 24px 0 rgba(255,255,255,0.08)"
      : undefined,
    transitionProperty: "box-shadow, transform, background-color, border-color"
  };

  return (
    <div
      className="group relative"
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onHover(null)}
    >
      {isHovered && (
        <div
          role="tooltip"
          className={`absolute ${isHeader ? "-bottom-8" : "-top-8"} pointer-events-none left-1/2 z-50 -translate-x-1/2 transform whitespace-nowrap rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs text-neutral-700 dark:border-neutral-900 dark:bg-neutral-800 dark:text-white`}
        >
          {item.label}
        </div>
      )}

      {"href" in item && item.href ? (
        <Link
          to={item.href}
          search={"search" in item ? item.search : undefined}
          aria-label={item.label}
          className={sharedClassName}
          style={sharedStyle}
        >
          {item.icon}
        </Link>
      ) : (
        <button
          type="button"
          onClick={"onClick" in item ? item.onClick : undefined}
          aria-label={item.label}
          className={sharedClassName}
          style={sharedStyle}
        >
          {item.icon}
        </button>
      )}
    </div>
  );
};

interface MinimalistDockProps {
  items: DockItem[];
  className?: string;
  variant?: "default" | "header";
}

const MinimalistDock: React.FC<MinimalistDockProps> = ({
  items,
  className = "",
  variant = "default"
}) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const containerClasses =
    variant === "header"
      ? "flex items-center gap-0.5 md:gap-1"
      : "flex items-center gap-2 p-3 rounded-2xl bg-black/20 backdrop-blur-sm border border-white/10 shadow-2xl";

  const wrapperClasses =
    variant === "header"
      ? `flex items-center justify-center ${className}`
      : `flex items-center justify-center p-4 ${className}`;

  return (
    <div className={wrapperClasses}>
      <div className={containerClasses}>
        {items.map((item) => (
          <DockItemComponent
            key={item.id}
            item={item}
            isHovered={hoveredItem === item.id}
            onHover={setHoveredItem}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
};

export default MinimalistDock;
